import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient, useQuery } from '@apollo/client'
import {
  ACADEMY_SCHEDULE_COACHES_QUERY,
  COACH_SCHEDULE_LAYER_QUERY,
} from './academy-schedule-queries'

const DEFAULT_CONCURRENCY = 4

function employmentToCoach(employment) {
  const coach = employment?.coach
  if (!coach?.id) return null
  return {
    id: coach.id,
    name: coach.name,
    role: employment.role,
    coachProfile: coach.coachProfile,
  }
}

function loadedCoachState(calendar) {
  return { loading: false, loaded: true, error: null, calendar }
}

function failedCoachState(error) {
  // `loaded: true` marks the fetch as finished so the loading gate clears;
  // callers can still read `error` to surface the failure.
  return { loading: false, loaded: true, error, calendar: [] }
}

function isCoachFetchSettled(state) {
  return Boolean(state?.loaded)
}

function buildRangeCoachStates(coachIds, rangeCache) {
  const states = {}
  for (const coachId of coachIds) {
    states[coachId] = rangeCache[coachId] || {
      loading: false,
      loaded: false,
      error: null,
      calendar: [],
    }
  }
  return states
}

async function fetchCoachCalendars({
  client,
  coachIds,
  from,
  to,
  rangeKey,
  rangeCache,
  idsToFetch,
  concurrency,
  signal,
  fetchGen,
  fetchGenRef,
}) {
  let index = 0
  const worker = async () => {
    while (index < idsToFetch.length) {
      if (signal.aborted || fetchGen !== fetchGenRef.current) {
        return
      }
      const coachId = idsToFetch[index++]
      try {
        const { data, error } = await client.query({
          query: COACH_SCHEDULE_LAYER_QUERY,
          variables: { coachId, from, to },
          fetchPolicy: 'no-cache',
          context: { fetchOptions: { signal } },
        })
        if (signal.aborted || fetchGen !== fetchGenRef.current) {
          return
        }
        if (error) throw error

        const state = loadedCoachState(data?.coach?.calendar || [])
        if (!rangeCache[rangeKey]) {
          rangeCache[rangeKey] = {}
        }
        rangeCache[rangeKey][coachId] = state
      } catch (err) {
        if (signal.aborted || fetchGen !== fetchGenRef.current) {
          return
        }
        if (!rangeCache[rangeKey]) {
          rangeCache[rangeKey] = {}
        }
        rangeCache[rangeKey][coachId] = failedCoachState(err)
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, idsToFetch.length) }, () =>
      worker(),
    ),
  )
}

/**
 * Academy employments for the coach picker — no calendar range fetching.
 */
export function useAcademyCoaches(academyId) {
  const {
    data: academyData,
    loading: academyLoading,
    error: academyError,
    refetch,
  } = useQuery(ACADEMY_SCHEDULE_COACHES_QUERY, {
    variables: { academyId },
    skip: !academyId,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
  })

  const allCoaches = useMemo(() => {
    const employments = academyData?.academy?.employments || []
    return employments
      .map(employmentToCoach)
      .filter(Boolean)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [academyData])

  return {
    academy: academyData?.academy,
    allCoaches,
    coachesLoading: academyLoading,
    error: academyError,
    refetch,
  }
}

/**
 * Fetches coach calendar layers for a date range (no academy employments query).
 */
export function useAcademyCoachCalendarLayers({
  from,
  to,
  coachIds,
  concurrency = DEFAULT_CONCURRENCY,
}) {
  const client = useApolloClient()
  const abortRef = useRef(null)
  const fetchGenRef = useRef(0)
  const cacheRef = useRef({})

  const coachIdsKey = coachIds.join(',')
  const rangeKey = `${from}|${to}`

  const [statesByRange, setStatesByRange] = useState({})
  const [rangeFetching, setRangeFetching] = useState(false)
  const [schedulesError, setSchedulesError] = useState(null)

  const coachStates = useMemo(() => statesByRange[rangeKey] || {}, [
    statesByRange,
    rangeKey,
  ])
  const hasRange = Boolean(from && to && coachIds.length)
  const allCoachesSettled =
    hasRange && coachIds.every(id => isCoachFetchSettled(coachStates[id]))

  const commitRangeFromCache = useCallback((rangeKeyForCommit, ids) => {
    const rangeCache = cacheRef.current[rangeKeyForCommit] || {}
    setStatesByRange(prev => ({
      ...prev,
      [rangeKeyForCommit]: buildRangeCoachStates(ids, rangeCache),
    }))
  }, [])

  const runRangeFetch = useCallback(
    async ({
      force = false,
      coachIdsForFetch,
      rangeKeyForFetch,
      fromIso,
      toIso,
    }) => {
      const ids = coachIdsForFetch || []
      if (!ids.length || !fromIso || !toIso) {
        setRangeFetching(false)
        return
      }

      const fetchGen = ++fetchGenRef.current

      if (force) {
        cacheRef.current[rangeKeyForFetch] = {}
      }

      const rangeCache = cacheRef.current
      const rangeBucket = rangeCache[rangeKeyForFetch] || {}
      const idsToFetch = force
        ? [...ids]
        : ids.filter(id => !isCoachFetchSettled(rangeBucket[id]))

      if (!idsToFetch.length) {
        commitRangeFromCache(rangeKeyForFetch, ids)
        setRangeFetching(false)
        return
      }

      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setRangeFetching(true)
      setSchedulesError(null)

      try {
        await fetchCoachCalendars({
          client,
          coachIds: ids,
          from: fromIso,
          to: toIso,
          rangeKey: rangeKeyForFetch,
          rangeCache,
          idsToFetch,
          concurrency,
          signal: controller.signal,
          fetchGen,
          fetchGenRef,
        })
      } catch (err) {
        if (!controller.signal.aborted && fetchGen === fetchGenRef.current) {
          setSchedulesError(err)
        }
      } finally {
        if (!controller.signal.aborted && fetchGen === fetchGenRef.current) {
          commitRangeFromCache(rangeKeyForFetch, ids)
          setRangeFetching(false)
        }
      }
    },
    [client, commitRangeFromCache, concurrency],
  )

  useEffect(() => {
    const ids = coachIdsKey ? coachIdsKey.split(',') : []
    if (!ids.length || !from || !to) {
      setRangeFetching(false)
      return undefined
    }

    const rangeCache = cacheRef.current[rangeKey] || {}
    const needsFetch = ids.some(id => !isCoachFetchSettled(rangeCache[id]))

    if (!needsFetch) {
      setStatesByRange(prev => {
        if (prev[rangeKey]) return prev
        return {
          ...prev,
          [rangeKey]: buildRangeCoachStates(ids, rangeCache),
        }
      })
      setRangeFetching(false)
      return undefined
    }

    setRangeFetching(true)
    runRangeFetch({
      coachIdsForFetch: ids,
      rangeKeyForFetch: rangeKey,
      fromIso: from,
      toIso: to,
    })

    return () => {
      abortRef.current?.abort()
    }
  }, [coachIdsKey, from, rangeKey, runRangeFetch, to])

  const coachOccurrencesMap = useMemo(() => {
    const map = {}
    for (const [coachId, state] of Object.entries(coachStates)) {
      if (state?.loaded) {
        map[coachId] = state.calendar || []
      }
    }
    return map
  }, [coachStates])

  const refetch = useCallback(async () => {
    const ids = coachIdsKey ? coachIdsKey.split(',') : []
    await runRangeFetch({
      force: true,
      coachIdsForFetch: ids,
      rangeKeyForFetch: rangeKey,
      fromIso: from,
      toIso: to,
    })
  }, [coachIdsKey, from, rangeKey, runRangeFetch, to])

  return {
    rangeFetching,
    calendarLoading: hasRange && (rangeFetching || !allCoachesSettled),
    error: schedulesError,
    coachStates,
    coachOccurrencesMap,
    refetch,
  }
}
